import { useQuery } from "react-query";
import { getAllSchoolClassByLicenseAsync, getAllSchoolClassCountAsync, getClassDashboardAsync, getClassNotificationStatsAsync, getCriticalStudentsAsync, getGlobalDashboardAsync, getLiveAttendanceAsync, getSchoolClassByIdAsync } from "services/SchoolClass";
import { ISchoolClassFilterParams } from "services/SchoolClass/types";
import { IPaginationParams } from "services/types";

export const SCHOOL_CLASS_KEYS = {
  ALL: 'schoolClass',
  LIST: 'list',
  COUNT: 'count',
  DETAIL: 'detail',
  LIVE_ATTENDANCE: 'live-attendance',
  NOTIFICATION_STATS: 'notification-stats',
  CRITICAL_STUDENTS: 'critical-students',
  DASHBOARD: 'dashboard',
  GLOBAL_DASHBOARD: 'global-dashboard',
};

const defaultQueryOptions = {
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60,
};

export const useGetAllSchoolClassByLicenseQuery = (
  pagination?: IPaginationParams,
  filter?: ISchoolClassFilterParams
) => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.LIST, pagination, filter], 
    () => getAllSchoolClassByLicenseAsync(pagination, filter),
    {
      ...defaultQueryOptions,
      keepPreviousData: true,
    }
  );
};

export const useGetSchoolClassCountQuery = (
  filter?: ISchoolClassFilterParams
) => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.COUNT, filter],
    () => getAllSchoolClassCountAsync(filter),
    {
      ...defaultQueryOptions,
      keepPreviousData: true, 
    }
  );
};

export const useGetSchoolClassByIdQuery = (id?: number | null) => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.DETAIL, id],
    () => getSchoolClassByIdAsync(id!),
    {
      ...defaultQueryOptions,
      enabled: !!id,
    }
  );
};

export const useGetLiveAttendanceQuery = (id?: number | null) => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.LIVE_ATTENDANCE, id],
    () => getLiveAttendanceAsync(id!),
    {
      ...defaultQueryOptions,
      enabled: typeof id === 'number' && id > 0,
      staleTime: 0, 
      refetchOnMount: 'always',
    }
  );
};

export const useGetClassNotificationStatsQuery = (id?: number | null) => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.NOTIFICATION_STATS, id],
    () => getClassNotificationStatsAsync(id!),
    {
      ...defaultQueryOptions,
      enabled: typeof id === 'number' && id > 0,
      staleTime: 1000 * 60 * 5, 
    }
  );
};

export const useGetCriticalStudentsQuery = (id?: number | null) => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.CRITICAL_STUDENTS, id],
    () => getCriticalStudentsAsync(id!),
    {
      refetchOnWindowFocus: false,
      enabled: typeof id === 'number' && id > 0,
      staleTime: 1000 * 60 * 2,
    }
  );
};

export const useGetClassDashboardQuery = (id?: number | null) => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.DASHBOARD, id],
    () => getClassDashboardAsync(id!),
    {
      ...defaultQueryOptions,
      staleTime: 1000 * 60 * 5,
      refetchInterval: 1000 * 60 * 5,
      enabled: typeof id === 'number' && id > 0, 
    }
  );
};


export const useGetGlobalDashboardQuery = () => {
  return useQuery(
    [SCHOOL_CLASS_KEYS.ALL, SCHOOL_CLASS_KEYS.GLOBAL_DASHBOARD],
    () => getGlobalDashboardAsync(),
    {
      ...defaultQueryOptions,
      staleTime: 1000 * 60 * 5,
      refetchInterval: 1000 * 60 * 5,
    }
  );
};